import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Sparkles,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { invokeErrorMessage } from '../../lib/supabaseFunctionError';
import {
  deleteMedia,
  isMediaUsedForConnection,
  listMediaWithPlatformUsage,
  mediaPreviewUrl,
  transformedImageUrl,
  uploadMedia,
  type MediaItemWithUsage,
} from '../../lib/mediaLibrary';
import {
  bakeCroppedImage,
  coverCropArea,
  IG_ASPECT_OPTIONS,
  IG_ASPECT_RATIOS,
  type IgAspectKey,
} from './SocialImageCropEditor';
import { useUpcomingSocialPostsSnippet } from '../../hooks/useUpcomingSocialPostsSnippet';
import { publishSocialTwin } from '../../lib/socialCrossPost';
import CrossPostToggle from './CrossPostToggle';

type BulkPlatform = 'facebook' | 'instagram';

type BulkStep = 'dates' | 'aspect' | 'generating' | 'review';

type BulkDraft = {
  dateKey: string;
  scheduledFor: string;
  sourceLibraryId: string;
  uploadedId: string;
  uploadedUrl: string;
  caption: string;
};

interface BulkCreateSocialPostsModalProps {
  platform: BulkPlatform;
  businessId: string;
  connectionId: string;
  onClose: () => void;
  /** Called after all posts are staged — parent should refresh + close the composer. */
  onStaged: () => void;
}

const DEFAULT_POST_HOUR = 12; // noon — the bulk-create default posting time

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Monday-first month grid, padded to full weeks (nulls = blank cells). */
function buildMonthCells(year: number, month: number): (string | null)[] {
  const startOffset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${year}-${pad2(month + 1)}-${pad2(d)}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/**
 * UTC instant for `hour`:00 local time on `dateKey` in the business timezone.
 * Iterating twice handles DST transitions; falls back to browser-local time.
 */
function zonedHourToUtc(dateKey: string, hour: number, tz: string | null): Date {
  const fallback = new Date(`${dateKey}T${pad2(hour)}:00:00`);
  if (!tz) return fallback;
  try {
    const desiredWall = Date.parse(`${dateKey}T${pad2(hour)}:00:00Z`);
    let ts = desiredWall;
    for (let i = 0; i < 2; i++) {
      const parts: Record<string, string> = {};
      new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
        .formatToParts(new Date(ts))
        .forEach((p) => {
          parts[p.type] = p.value;
        });
      const mappedWall = Date.parse(
        `${parts.year}-${parts.month}-${parts.day}T${parts.hour === '24' ? '00' : parts.hour}:${parts.minute}:${parts.second}Z`,
      );
      if (Number.isNaN(mappedWall)) return fallback;
      ts += desiredWall - mappedWall;
    }
    return new Date(ts);
  } catch {
    return fallback;
  }
}

function loadImageDims(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

function fmtDayLabel(dateKey: string): string {
  const d = new Date(`${dateKey}T12:00:00`);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtScheduleLabel(iso: string, tz: string | null): string {
  const d = new Date(iso);
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(tz ? { timeZone: tz } : {}),
  };
  try {
    return d.toLocaleString(undefined, opts);
  } catch {
    return d.toLocaleString();
  }
}

const MONTH_LABEL: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export default function BulkCreateSocialPostsModal({
  platform,
  businessId,
  connectionId,
  onClose,
  onStaged,
}: BulkCreateSocialPostsModalProps) {
  const platformLabel = platform === 'instagram' ? 'Instagram' : 'Facebook';
  const otherPlatform: BulkPlatform = platform === 'instagram' ? 'facebook' : 'instagram';
  const otherLabel = otherPlatform === 'instagram' ? 'Instagram' : 'Facebook';

  const [step, setStep] = useState<BulkStep>('dates');
  const [monthCursor, setMonthCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set());
  const [aspectKey, setAspectKey] = useState<IgAspectKey>('1:1');

  const [unusedImages, setUnusedImages] = useState<MediaItemWithUsage[] | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);

  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [drafts, setDrafts] = useState<BulkDraft[]>([]);
  const [shortage, setShortage] = useState(0);
  const [genError, setGenError] = useState<string | null>(null);

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [staging, setStaging] = useState(false);
  const [otherConnectionId, setOtherConnectionId] = useState<string | null>(null);
  const [alsoPostToOther, setAlsoPostToOther] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('social_media_connections')
        .select('id')
        .eq('business_id', businessId)
        .eq('platform', otherPlatform)
        .eq('provider', 'ayrshare')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      if (!cancelled) setOtherConnectionId((data?.id as string) || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, otherPlatform]);

  const cancelledRef = useRef(false);
  /** Crops uploaded this session — soft-deleted on abandon, kept once staged. */
  const uploadedIdsRef = useRef<Set<string>>(new Set());
  const stagedUploadIdsRef = useRef<Set<string>>(new Set());

  // Existing calendar content so the user can see which days already have posts.
  const { posts: existingPosts } = useUpcomingSocialPostsSnippet({
    platform,
    includePublished: true,
    limit: 200,
  });
  const dayContent = useMemo(() => {
    const map = new Map<string, { pending: boolean; published: boolean }>();
    for (const p of existingPosts) {
      const when = p.scheduled_for || p.created_at;
      if (!when) continue;
      const key = toDateKey(new Date(when));
      const entry = map.get(key) || { pending: false, published: false };
      if (p.status === 'published') entry.published = true;
      else entry.pending = true;
      map.set(key, entry);
    }
    return map;
  }, [existingPosts]);

  // Unused-image budget + business timezone, loaded once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [media, bizRes] = await Promise.all([
          listMediaWithPlatformUsage(businessId),
          supabase.from('businesses').select('timezone').eq('id', businessId).maybeSingle(),
        ]);
        if (cancelled) return;
        setUnusedImages(
          media.filter(
            (m) =>
              (m.mime_type || '').startsWith('image/') &&
              !isMediaUsedForConnection(m, connectionId),
          ),
        );
        setTimezone((bizRes.data?.timezone as string) || null);
      } catch (err) {
        console.error('Failed to load media for bulk create:', err);
        if (!cancelled) setUnusedImages([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, connectionId]);

  useEffect(
    () => () => {
      cancelledRef.current = true;
    },
    [],
  );

  const cleanupUnstagedUploads = () => {
    const toDelete = [...uploadedIdsRef.current].filter(
      (id) => !stagedUploadIdsRef.current.has(id),
    );
    for (const id of toDelete) void deleteMedia(id).catch(() => undefined);
    uploadedIdsRef.current.clear();
  };

  const handleClose = () => {
    if (step === 'generating') {
      if (!window.confirm('Stop building posts? Progress will be discarded.')) return;
    } else if (step === 'review' && drafts.length > 0 && !staging) {
      if (!window.confirm('Discard these posts without staging them?')) return;
    }
    cancelledRef.current = true;
    cleanupUnstagedUploads();
    onClose();
  };

  const todayKey = toDateKey(new Date());

  const toggleDay = (key: string) => {
    if (key < todayKey) return;
    setSelectedDays((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const scheduleIsoForDay = (dateKey: string): string => {
    let when = zonedHourToUtc(dateKey, DEFAULT_POST_HOUR, timezone);
    // Today after noon: staging rejects past times, so push to shortly from now.
    if (when.getTime() <= Date.now() + 5 * 60_000) {
      when = new Date(Date.now() + 30 * 60_000);
    }
    return when.toISOString();
  };

  const runGeneration = async () => {
    const days = [...selectedDays].sort();
    const images = unusedImages || [];
    const count = Math.min(days.length, images.length);
    cancelledRef.current = false;
    setDrafts([]);
    setGenError(null);
    setShortage(days.length - count);
    setProgress({ done: 0, total: count });
    setStep('generating');

    const built: BulkDraft[] = [];
    try {
      for (let i = 0; i < count; i++) {
        if (cancelledRef.current) return;
        const dateKey = days[i];
        const source = images[i];

        // Center-crop to the chosen aspect and upload the baked frame. Crop
        // coords are computed against the same preview-size URL we bake from.
        const previewUrl = mediaPreviewUrl(source.url);
        const dims = await loadImageDims(previewUrl);
        const area = coverCropArea(dims.width, dims.height, IG_ASPECT_RATIOS[aspectKey]);
        const blob = await bakeCroppedImage(previewUrl, area, {
          maxEdge: 1080,
          aspect: IG_ASPECT_RATIOS[aspectKey],
        });
        const uploaded = await uploadMedia(
          businessId,
          [new File([blob], `bulk-${dateKey}.jpg`, { type: 'image/jpeg' })],
          { source: 'social_crop' },
        );
        const crop = uploaded[0];
        if (!crop) throw new Error('Upload failed');
        uploadedIdsRef.current.add(crop.id);
        if (cancelledRef.current) return;

        const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
          body: {
            action: 'compose_caption',
            businessId,
            connectionId,
            platform,
            imageUrls: [crop.url],
          },
        });
        if (error || data?.error || !String(data?.content || '').trim()) {
          const msg =
            data?.error ||
            (await invokeErrorMessage(error, data)) ||
            'Failed to write caption';
          if (data?.code === 'insufficient_tokens' || /token/i.test(String(msg))) {
            setGenError(
              built.length > 0
                ? `Ran out of AI tokens after ${built.length} ${built.length === 1 ? 'post' : 'posts'} — you can stage what was created.`
                : 'Not enough AI tokens — purchase more in AI Settings.',
            );
          } else {
            setGenError(
              built.length > 0
                ? `Stopped after ${built.length} ${built.length === 1 ? 'post' : 'posts'}: ${msg}`
                : msg,
            );
          }
          break;
        }
        if (cancelledRef.current) return;

        built.push({
          dateKey,
          scheduledFor: scheduleIsoForDay(dateKey),
          sourceLibraryId: source.id,
          uploadedId: crop.id,
          uploadedUrl: crop.url,
          caption: String(data.content).trim(),
        });
        setDrafts([...built]);
        setProgress({ done: built.length, total: count });
      }
    } catch (err) {
      console.error('Bulk generation error:', err);
      setGenError(
        built.length > 0
          ? `Stopped after ${built.length} ${built.length === 1 ? 'post' : 'posts'} — ${err instanceof Error ? err.message : 'something went wrong'}.`
          : err instanceof Error
            ? err.message
            : 'Failed to build posts',
      );
    }

    if (cancelledRef.current) return;
    if (built.length === 0 && !genError) {
      // Loop never ran (no images) — shouldn't happen since Next is gated.
      setGenError('No unused images available.');
    }
    setStep('review');
  };

  const handleStageAll = async () => {
    if (drafts.length === 0 || staging) return;
    setStaging(true);
    let stagedCount = 0;
    let crossCount = 0;
    let firstError: string | null = null;
    let firstCrossError: string | null = null;
    const remaining: BulkDraft[] = [];
    const mirror = alsoPostToOther && !!otherConnectionId;
    try {
      for (const draft of drafts) {
        const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
          body: {
            action: 'stage_manual',
            businessId,
            connectionId,
            platform,
            content: draft.caption,
            mediaIds: [draft.uploadedId],
            mediaId: draft.uploadedId,
            scheduledFor: draft.scheduledFor,
            sourceLibraryMediaIds: [draft.sourceLibraryId],
          },
        });
        if (error || data?.error) {
          if (!firstError) {
            firstError =
              data?.error || (await invokeErrorMessage(error, data)) || 'Failed to stage post';
          }
          remaining.push(draft);
          continue;
        }
        stagedCount += 1;
        stagedUploadIdsRef.current.add(draft.uploadedId);
        if (!mirror || !otherConnectionId) continue;
        const twin = await publishSocialTwin({
          mode: 'schedule',
          businessId,
          connectionId: otherConnectionId,
          platform: otherPlatform,
          content: draft.caption,
          mediaIds: [draft.uploadedId],
          scheduledFor: draft.scheduledFor,
          sourceLibraryMediaIds: [draft.sourceLibraryId],
        });
        if (twin.ok) crossCount += 1;
        else if (!firstCrossError) firstCrossError = twin.message;
      }
    } finally {
      setStaging(false);
    }

    if (remaining.length === 0) {
      toast.success(
        mirror
          ? `Staged ${stagedCount} ${stagedCount === 1 ? 'post' : 'posts'} on ${platformLabel}${
              crossCount ? ` and ${otherLabel}` : ''
            }`
          : `Staged ${stagedCount} ${stagedCount === 1 ? 'post' : 'posts'}`,
      );
      if (firstCrossError) {
        toast.error(`${otherLabel} did not get every post: ${firstCrossError}`);
      }
      cleanupUnstagedUploads();
      onStaged();
      return;
    }
    setDrafts(remaining);
    setExpandedIndex(null);
    toast.error(
      `Staged ${stagedCount} of ${stagedCount + remaining.length} — ${firstError || 'some posts failed'}`,
    );
    if (firstCrossError) {
      toast.error(`${otherLabel} did not get every post: ${firstCrossError}`);
    }
  };

  const cells = buildMonthCells(monthCursor.year, monthCursor.month);
  const monthLabel = new Date(monthCursor.year, monthCursor.month, 1).toLocaleDateString(
    undefined,
    MONTH_LABEL,
  );
  const atCurrentMonth =
    monthCursor.year === new Date().getFullYear() && monthCursor.month === new Date().getMonth();

  const unusedCount = unusedImages?.length ?? null;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const stepTitle =
    step === 'dates'
      ? 'Pick your days'
      : step === 'aspect'
        ? 'Choose aspect ratio'
        : step === 'generating'
          ? 'Building posts'
          : 'Review posts';

  const expandedDraft = expandedIndex !== null ? drafts[expandedIndex] : null;
  const crossPostToggle =
    otherConnectionId && step !== 'generating' ? (
      <div className="pb-3">
        <CrossPostToggle
          platform={otherPlatform}
          checked={alsoPostToOther}
          onChange={setAlsoPostToOther}
          disabled={staging}
        />
      </div>
    ) : null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/50"
        style={{ zIndex: 100230, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div
          className={`bg-white shadow-2xl flex flex-col overflow-hidden ${
            isMobile ? 'fixed inset-0 w-full' : 'fixed inset-y-0 right-0 w-full sm:w-[520px]'
          }`}
          style={{
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            zIndex: 100240,
            top: 'env(safe-area-inset-top, 0px)',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            ...(!isMobile && {
              height:
                'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            }),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              {step === 'aspect' ? (
                <button
                  type="button"
                  onClick={() => setStep('dates')}
                  className="p-1.5 -ml-1.5 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              ) : null}
              <div className="min-w-0">
                <h2 className="text-[15px] font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                  Bulk create with AI
                </h2>
                <p className="text-xs text-gray-400 truncate">
                  {platformLabel} · {stepTitle}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div
            className="flex-1 overflow-y-auto p-4 sm:p-5"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {step === 'dates' ? (
              <div className="space-y-4">
                <p className="text-[13px] text-gray-500 leading-relaxed">
                  Tap every day you want a post on — one post per day. AI will build a{' '}
                  {platformLabel} post for each, scheduled at noon.
                </p>

                {unusedCount !== null ? (
                  unusedCount === 0 ? (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-2.5 text-[12px] text-amber-800 leading-relaxed">
                      No unused photos left in your Media library for this {platformLabel}{' '}
                      account. Upload new photos first, then bulk create.
                    </div>
                  ) : (
                    <div className="rounded-xl bg-blue-50 border border-blue-100 px-3.5 py-2.5 text-[12px] text-blue-800 leading-relaxed">
                      {unusedCount} unused {unusedCount === 1 ? 'photo' : 'photos'} available —
                      enough for up to {unusedCount} {unusedCount === 1 ? 'post' : 'posts'}.
                    </div>
                  )
                ) : null}

                {/* Month navigation */}
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() =>
                      setMonthCursor((c) => {
                        const d = new Date(c.year, c.month - 1, 1);
                        return { year: d.getFullYear(), month: d.getMonth() };
                      })
                    }
                    disabled={atCurrentMonth}
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm font-semibold text-gray-900">{monthLabel}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setMonthCursor((c) => {
                        const d = new Date(c.year, c.month + 1, 1);
                        return { year: d.getFullYear(), month: d.getMonth() };
                      })
                    }
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>

                {/* Calendar grid */}
                <div>
                  <div className="grid grid-cols-7 mb-1">
                    {WEEKDAYS.map((d, i) => (
                      <span
                        key={`${d}-${i}`}
                        className="text-center text-[11px] font-medium text-gray-400"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-y-1">
                    {cells.map((key, idx) => {
                      if (!key) return <span key={`blank-${idx}`} />;
                      const day = Number(key.slice(-2));
                      const past = key < todayKey;
                      const selected = selectedDays.has(key);
                      const content = dayContent.get(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleDay(key)}
                          disabled={past}
                          className="flex flex-col items-center gap-0.5 py-0.5 group disabled:cursor-not-allowed"
                        >
                          <span
                            className={`h-9 w-9 rounded-full inline-flex items-center justify-center text-[13px] font-medium transition-colors ${
                              selected
                                ? 'bg-blue-600 text-white'
                                : past
                                  ? 'text-gray-300'
                                  : key === todayKey
                                    ? 'text-blue-600 font-semibold group-hover:bg-blue-50'
                                    : 'text-gray-700 group-hover:bg-gray-100'
                            }`}
                          >
                            {day}
                          </span>
                          <span className="h-1.5 flex items-center gap-0.5">
                              {content?.pending ? (
                                <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
                              ) : null}
                              {content?.published ? (
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                              ) : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> Scheduled
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Posted
                    </span>
                  </div>
                </div>

                {unusedCount !== null && selectedDays.size > unusedCount && unusedCount > 0 ? (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-2.5 text-[12px] text-amber-800 leading-relaxed">
                    You picked {selectedDays.size} days but only {unusedCount} unused{' '}
                    {unusedCount === 1 ? 'photo is' : 'photos are'} available — the AI will fill
                    the earliest {unusedCount} {unusedCount === 1 ? 'day' : 'days'} and stop.
                  </div>
                ) : null}
              </div>
            ) : step === 'aspect' ? (
              <div className="space-y-4">
                <p className="text-[13px] text-gray-500 leading-relaxed">
                  Every image will be cropped to this ratio before posting.
                </p>
                <div className="space-y-2.5">
                  {IG_ASPECT_OPTIONS.map((opt) => {
                    const ratio = IG_ASPECT_RATIOS[opt.id];
                    const active = aspectKey === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setAspectKey(opt.id)}
                        className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl border text-left transition-all ${
                          active
                            ? 'border-blue-500 bg-blue-50/60 ring-1 ring-blue-500/30'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <span className="h-14 w-14 inline-flex items-center justify-center shrink-0">
                          <span
                            className={`rounded-md border-2 ${
                              active ? 'border-blue-500 bg-blue-100' : 'border-gray-300 bg-gray-100'
                            }`}
                            style={
                              ratio >= 1
                                ? { width: 44, height: Math.round(44 / ratio) }
                                : { height: 44, width: Math.round(44 * ratio) }
                            }
                          />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[14px] font-semibold text-gray-900">
                            {opt.label}
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">{opt.id}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : step === 'generating' ? (
              <div className="flex flex-col items-center justify-center text-center py-14">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-5 animate-pulse">
                  <Sparkles className="h-8 w-8 text-blue-500" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  AI is building your posts
                </h3>
                <p className="text-[13px] text-gray-500 mt-1.5">
                  {progress.total > 0
                    ? `Post ${Math.min(progress.done + 1, progress.total)} of ${progress.total}`
                    : 'Preparing…'}
                </p>
                <div className="w-56 h-1.5 bg-gray-100 rounded-full overflow-hidden mt-5">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-500"
                    style={{
                      width: `${progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0}%`,
                    }}
                  />
                </div>
                {drafts.length > 0 ? (
                  <div className="flex -space-x-2 mt-6">
                    {drafts.slice(-5).map((d) => (
                      <img
                        key={d.uploadedId}
                        src={transformedImageUrl(d.uploadedUrl, { width: 96 })}
                        alt=""
                        className="h-10 w-10 rounded-lg object-cover ring-2 ring-white"
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="space-y-4">
                {shortage > 0 && drafts.length > 0 ? (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-2.5 text-[12px] text-amber-800 leading-relaxed">
                    There weren't enough unused images — created {drafts.length} of{' '}
                    {drafts.length + shortage} posts. The remaining days were skipped.
                  </div>
                ) : null}
                {genError ? (
                  <div className="rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5 text-[12px] text-red-700 leading-relaxed">
                    {genError}
                  </div>
                ) : null}

                {drafts.length > 0 ? (
                  <>
                    <p className="text-[13px] text-gray-500">
                      Tap a post to read its caption. Everything gets staged at noon on its day.
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {drafts.map((d, i) => (
                        <button
                          key={d.uploadedId}
                          type="button"
                          onClick={() => setExpandedIndex(i)}
                          className="relative rounded-xl overflow-hidden border border-gray-200 hover:border-blue-400 transition-colors group"
                          style={{ aspectRatio: '1 / 1' }}
                        >
                          <img
                            src={transformedImageUrl(d.uploadedUrl, { width: 480 })}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                          <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 pb-1 pt-4 text-left">
                            <span className="block text-[10px] font-semibold text-white leading-tight">
                              {fmtDayLabel(d.dateKey)}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-10">
                    No posts were created.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {step === 'dates' ? (
            <div className="border-t border-gray-100 p-4 sm:p-5 flex-shrink-0">
              {crossPostToggle}
              <div className="flex items-center justify-between gap-3">
              <span className="text-[13px] text-gray-500">
                {selectedDays.size} {selectedDays.size === 1 ? 'day' : 'days'} selected
              </span>
              <button
                type="button"
                onClick={() => setStep('aspect')}
                disabled={selectedDays.size === 0 || unusedCount === 0 || unusedCount === null}
                className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
              </div>
            </div>
          ) : step === 'aspect' ? (
            <div className="border-t border-gray-100 p-4 sm:p-5 flex-shrink-0">
              {crossPostToggle}
              <div className="flex items-center justify-between gap-3">
              <span className="text-[13px] text-gray-500">
                {Math.min(selectedDays.size, unusedCount ?? 0)}{' '}
                {Math.min(selectedDays.size, unusedCount ?? 0) === 1 ? 'post' : 'posts'} will be
                created
              </span>
              <button
                type="button"
                onClick={() => void runGeneration()}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 transition-colors"
              >
                <Sparkles className="h-4 w-4" />
                Start creating
              </button>
              </div>
            </div>
          ) : step === 'review' && drafts.length > 0 ? (
            <div className="border-t border-gray-100 p-4 sm:p-5 flex-shrink-0">
              {crossPostToggle}
              <button
                type="button"
                onClick={() => void handleStageAll()}
                disabled={staging}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white text-[14px] font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {staging ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {staging
                  ? 'Staging…'
                  : alsoPostToOther && otherConnectionId
                    ? `Finish — stage ${drafts.length} on ${platformLabel} & ${otherLabel}`
                    : `Finish — stage ${drafts.length} ${drafts.length === 1 ? 'post' : 'posts'}`}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Expanded post detail */}
      {expandedDraft ? (
        <div
          className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center sm:p-6"
          style={{ zIndex: 100250 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setExpandedIndex(null);
          }}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
              <span className="text-sm font-semibold text-gray-900">
                {fmtScheduleLabel(expandedDraft.scheduledFor, timezone)}
              </span>
              <button
                type="button"
                onClick={() => setExpandedIndex(null)}
                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <img
                src={transformedImageUrl(expandedDraft.uploadedUrl, { width: 900 })}
                alt=""
                className="w-full object-cover"
                style={{ aspectRatio: String(IG_ASPECT_RATIOS[aspectKey]) }}
              />
              <div className="p-4">
                <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">
                  Caption
                </label>
                <textarea
                  value={expandedDraft.caption}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDrafts((prev) =>
                      prev.map((d, i) => (i === expandedIndex ? { ...d, caption: value } : d)),
                    );
                  }}
                  rows={6}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] text-gray-800 leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  style={{ fontSize: '14px' }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>,
    document.body,
  );
}
